import React, {
  createContext,
  useEffect,
  useMemo,
  useReducer,
  useContext,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { useConfig } from "./ConfigContext";
import { useDispatch } from "react-redux";
import {
  setCurrentUser,
  clearCurrentUser,
  setPlayerBalance,
  clearPlayerBalance,
  setWheelPoint,
  clearWheelPoint,
  setPlayerCash,
  clearPlayerCash,
} from "../store/slices/user";
import {
  saveStoredUser,
  loadStoredUser,
  clearStoredUser,
  extractToken,
  extractRefreshToken,
  setSession,
  decodeAndCheckExpiry,
} from "../utils/authSession";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  token: null,
  refreshToken: null,
};

const handlers = {
  INITIALIZE: (s, a) => ({ ...s, isInitialized: true, ...a.payload }),
  LOGIN: (s, a) => ({
    ...s,
    isAuthenticated: true,
    user: a.payload.user,
    token: a.payload.token,
    refreshToken: a.payload.refreshToken,
  }),
  SET_TOKEN: (s, a) => ({ ...s, token: a.payload.token }),
  LOGOUT: (s) => ({
    ...s,
    isAuthenticated: false,
    user: null,
    token: null,
    refreshToken: null,
  }),
};
const reducer = (s, a) => (handlers[a.type] ? handlers[a.type](s, a) : s);

function getComparableUserSignature(user) {
  if (!user) return "";
  return JSON.stringify([
    user.id ?? null,
    user.username ?? null,
    user.book_name ?? user.name ?? null,
    user.status ?? null,
    Number(user.credit_balance ?? user.credit ?? 0),
    user.book_number ?? null,
    user.bank_name ?? null,
    user.PromotionID ?? null,
    user.PromotionDetail ?? null,
    user.PromotionStatus ?? null,
    user.withdrawConditionType ?? null,
    user.withdrawConditionDetail ?? null,
  ]);
}

const JWTContext = createContext({
  ...initialState,
  login: async () => {},
  logout: () => {},
  refreshAccessToken: async () => {},
  getAccessToken: () => null,
});

export const JWTProvider = ({ children }) => {
  const { apiBaseUrl } = useConfig();
  const reduxDispatch = useDispatch();
  const [state, dispatch] = useReducer(reducer, initialState);
  const authStateRef = useRef(initialState);
  const validatingRef = useRef(false);
  const lastValidateRef = useRef(0);
  const refreshPromiseRef = useRef(null);
  const VALIDATE_INTERVAL_MS = 60 * 1000;
  useEffect(() => void (authStateRef.current = state), [state]);

  const getStoredToken = useCallback(() => {
    return (
      (typeof window !== "undefined" && localStorage.getItem("authToken")) ||
      (typeof window !== "undefined" && localStorage.getItem("accessToken")) ||
      null
    );
  }, []);

  const setAuthTokenEverywhere = useCallback((token) => {
    if (typeof window !== "undefined") {
      if (token) {
        try {
          localStorage.setItem("authToken", token); // baseline key
          localStorage.setItem("accessToken", token); // keep legacy key in sync
        } catch {}
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      } else {
        try {
          localStorage.removeItem("authToken");
          localStorage.removeItem("accessToken");
        } catch {}
        delete axios.defaults.headers.common.Authorization;
      }
    }
  }, []);

  const fetchPlayerBalance = useCallback(
    async (id) => {
      try {
        if (!id) return;
        const r = await axios.get(`${apiBaseUrl}/userbalance/balance/${id}`);
        const bal =
          typeof r?.data === "number"
            ? r.data
            : (r?.data?.balance ?? r?.data?.data ?? 0);
        reduxDispatch(setPlayerBalance(Number(bal) || 0));
      } catch {
        reduxDispatch(setPlayerBalance(0));
      }
    },
    [apiBaseUrl, reduxDispatch],
  );
  const fetchWheelPoint = useCallback(
    async (id) => {
      try {
        if (!id) return;
        const r = await axios.get(`${apiBaseUrl}/wheel/point/${id}`);
        const w =
          typeof r?.data === "number"
            ? r.data
            : (r.data.data[0]?.wheelPoint ?? 0);
        reduxDispatch(setWheelPoint(Number(w) || 0));
      } catch {
        reduxDispatch(setWheelPoint(0));
      }
    },
    [apiBaseUrl, reduxDispatch],
  );
  const fetchPlayerCash = useCallback(
    async (id) => {
      try {
        if (!id) return;
        const r = await axios.get(`${apiBaseUrl}/userbalance/cash/${id}`);
        const cash =
          typeof r?.data === "number"
            ? r.data
            : (r?.data?.cashWallet ?? r?.data?.data ?? 0);
        reduxDispatch(setPlayerCash(Number(cash) || 0));
      } catch {
        reduxDispatch(setPlayerCash(0));
      }
    },
    [apiBaseUrl, reduxDispatch],
  );

  const hardLogout = useCallback(() => {
    setSession(null, null);
    setAuthTokenEverywhere(null);
    try {
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("currentUser");
      localStorage.removeItem("userId");
    } catch {}
    clearStoredUser();
    dispatch({ type: "LOGOUT" });
    reduxDispatch(clearCurrentUser());
    reduxDispatch(clearPlayerBalance());
    reduxDispatch(clearWheelPoint());
    reduxDispatch(clearPlayerCash());
  }, [reduxDispatch, setAuthTokenEverywhere]);
  const syncUser = useCallback(
    (u) => {
      if (!u) return;
      reduxDispatch(setCurrentUser(u));
      fetchPlayerBalance(u.id);
      fetchWheelPoint(u.id);
      fetchPlayerCash(u.id);
    },
    [reduxDispatch, fetchPlayerBalance, fetchWheelPoint, fetchPlayerCash],
  );

  const refreshAccessToken = useCallback(async () => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    const rt =
      typeof window !== "undefined" && localStorage.getItem("refreshToken");
    if (!rt) return null;
    const exec = async () => {
      try {
        const res = await axios.post(`${apiBaseUrl}/playerlogin/refresh`, {
          refreshToken: rt,
        });
        const newToken = extractToken(res) || res?.data?.accessToken;
        const newRefresh = extractRefreshToken(res) || rt; // keep old if not provided
        if (newToken) {
          setSession(newToken, newRefresh);
          setAuthTokenEverywhere(newToken);
          dispatch({ type: "SET_TOKEN", payload: { token: newToken } });
          return newToken;
        }
        return null;
      } catch {
        hardLogout();
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    };
    refreshPromiseRef.current = exec();
    return refreshPromiseRef.current;
  }, [apiBaseUrl, hardLogout, setAuthTokenEverywhere]);

  const validateSession = useCallback(
    async (force = false) => {
      if (validatingRef.current) return;
      const token = getStoredToken();
      if (!token) return;
      const now = Date.now();
      if (!force && now - lastValidateRef.current < VALIDATE_INTERVAL_MS)
        return;
      lastValidateRef.current = now;
      validatingRef.current = true;
      try {
        const { expired } = decodeAndCheckExpiry(token);
        if (expired) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) return; // hardLogout triggered inside refreshAccessToken
        }
        await axios
          .post(`${apiBaseUrl}/playerlogin/check/`, {
            token: getStoredToken(),
          })
          .then((res) => {
            if (
              res.data === "notfound" ||
              res.data === "banned" ||
              !res.data?.id
            ) {
              hardLogout();
            } else {
              const u = res.data;
              const currentAuthState = authStateRef.current;
              const previousUser = currentAuthState.user || loadStoredUser();
              const shouldSyncUser =
                force ||
                !currentAuthState.isAuthenticated ||
                getComparableUserSignature(previousUser) !==
                  getComparableUserSignature(u);

              try {
                if (u?.id) localStorage.setItem("userId", String(u.id));
              } catch {}

              if (shouldSyncUser) {
                saveStoredUser(u);
                dispatch({
                  type: "LOGIN",
                  payload: {
                    user: u,
                    token: getStoredToken(),
                    refreshToken:
                      typeof window !== "undefined"
                        ? localStorage.getItem("refreshToken")
                        : null,
                  },
                });
                syncUser(u);
              }
            }
          })
          .catch(async (err) => {
            if (err?.response?.status === 401) {
              const refreshed = await refreshAccessToken();
              if (!refreshed) {
                hardLogout();
                return;
              }
            } else {
              // transient/network -> do not logout immediately
            }
          });
      } finally {
        validatingRef.current = false;
      }
    },
    [
      apiBaseUrl,
      refreshAccessToken,
      hardLogout,
      syncUser,
      VALIDATE_INTERVAL_MS,
      getStoredToken,
    ],
  );

  useEffect(() => {
    const init = async () => {
      try {
        const token = getStoredToken();
        const rTok =
          typeof window !== "undefined" && localStorage.getItem("refreshToken");
        const storedUser = loadStoredUser();
        if (token) {
          setSession(token, rTok);
          setAuthTokenEverywhere(token);
          const { expired } = decodeAndCheckExpiry(token);
          if (expired) {
            const refreshed = await refreshAccessToken();
            if (!refreshed) {
              hardLogout();
              dispatch({
                type: "INITIALIZE",
                payload: {
                  isAuthenticated: false,
                  user: null,
                  token: null,
                  refreshToken: null,
                },
              });
              return;
            }
          }
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user: storedUser,
              token,
              refreshToken: rTok,
            },
          });
          if (storedUser?.id) {
            try {
              localStorage.setItem("userId", String(storedUser.id));
            } catch {}
          }
          syncUser(storedUser);
          setTimeout(() => validateSession(true), 300);
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
              token: null,
              refreshToken: null,
            },
          });
        }
      } catch {
        setSession(null, null);
        setAuthTokenEverywhere(null);
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
          },
        });
      }
    };
    init();
  }, [
    refreshAccessToken,
    syncUser,
    validateSession,
    hardLogout,
    getStoredToken,
    setAuthTokenEverywhere,
  ]);

  // Axios interceptors
  useEffect(() => {
    const reqId = axios.interceptors.request.use((cfg) => {
      const t = getStoredToken();
      if (t) cfg.headers.Authorization = `Bearer ${t}`;
      return cfg;
    });

    const resId = axios.interceptors.response.use(
      (r) => r,
      async (err) => {
        const original = err.config;
        if (err?.response?.status === 401 && !original?._retry) {
          original._retry = true;
          const t = await refreshAccessToken();
          if (t) {
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${t}`;
            return axios(original);
          } else {
            hardLogout();
          }
        }
        return Promise.reject(err);
      },
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, [refreshAccessToken, hardLogout, getStoredToken]);

  // Focus & visibility revalidation
  useEffect(() => {
    const onFocus = () => validateSession(false);
    const onVis = () => {
      if (document.visibilityState === "visible") validateSession(false);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [validateSession]);
  useEffect(() => {
    const id = setInterval(() => validateSession(false), VALIDATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [validateSession, VALIDATE_INTERVAL_MS]);

  const login = useCallback(
    async (username, password) => {
      const res = await axios.post(`${apiBaseUrl}/playerlogin`, {
        username,
        password,
      });
      const token =
        extractToken(res) || res?.data?.sessionToken || res?.data?.accessToken;
      const rTok = extractRefreshToken(res) || res?.data?.refreshToken;
      if (!token)
        throw new Error(
          res?.data?.message || res?.data?.error || "Login failed",
        );
      const user = res?.data || null;
      saveStoredUser(user);
      if (user?.id) {
        try {
          localStorage.setItem("userId", String(user.id));
        } catch {}
      }
      setSession(token, rTok);
      setAuthTokenEverywhere(token);
      dispatch({
        type: "LOGIN",
        payload: { user, token, refreshToken: rTok },
      });
      syncUser(user);
      return { user, token };
    },
    [apiBaseUrl, syncUser, setAuthTokenEverywhere],
  );

  const logout = useCallback(async () => {
    try {
      const token = getStoredToken();
      if (token) {
        await axios.post(`${apiBaseUrl}/playerlogin/logout`, { token });
      }
    } catch {}
    hardLogout();
  }, [apiBaseUrl, hardLogout, getStoredToken]);

  const getAccessToken = useCallback(() => getStoredToken(), [getStoredToken]);

  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
      refreshAccessToken,
      getAccessToken,
    }),
    [state, login, logout, refreshAccessToken, getAccessToken],
  );
  return <JWTContext.Provider value={value}>{children}</JWTContext.Provider>;
};
export const useAuth = () => useContext(JWTContext);
export default JWTContext;
